import React, { useEffect } from "react";
import { useMap } from "react-map-gl/mapbox";
import { componentLabel } from "../components/CommandCenter/Helpers/ComponentMappings";
import JobsiteOverview from "../components/Project/ProjectPortal/JobsiteOverview";
import Helper from "../Helper";
import { CommandCenterSection } from "../models/CommandCenterSection";
import Weather from "../Weather";
import CommandDayCalendar from "./CommandCalendarComponents/CommandDayCalendar";
import CommandListView from "./CommandCalendarComponents/CommandListView";
import CommandWeekCalendar from "./CommandCalendarComponents/CommandWeekCalendar";
import CommandCenterIframe from "./CommandCenterIframe";
import CommandCustomText from "./CommandCustomText";
import CommandImage from "./CommandImage";
import CommandCenterMap from "./CommandMapComponents/CommandCenterMap";
import CommandCenterMapContainer from "./CommandMapComponents/CommandCenterMapContainer";
import CommandMapLegend from "./CommandMapComponents/CommandMapLegend";
import { CommandProjectInfo } from "./CommandTypes";
import DashboardCurrentConditions from "./DashboardWeatherComponents/DashboardCurrentConditions";
import DashboardWeather from "./DashboardWeatherComponents/DashboardWeather";
import { JobsiteCommandContext } from "./JobsiteCommandProvider";
import CommandMilestones from "./Milestones/CommandMilestones";
import DashboardProcurementChecklist from "./ProcurementChecklist/dashboardProcurementChecklist";

type CommandCenterColumnViewProps = {
    projectInfo: CommandProjectInfo;
    commandCenterSection: CommandCenterSection | undefined;
};

const CommandCenterColumnView: React.FC<CommandCenterColumnViewProps> = ({ projectInfo, commandCenterSection }) => {
    const context = React.useContext(JobsiteCommandContext);
    const [currentTab, setCurrentTab] = React.useState(0);
    const [currentMapTab, setCurrentMapTab] = React.useState(0);

    const fetchWeatherData = (): Promise<any> => {
        return Weather.getWeatherInformation(projectInfo.unique_token).then((response) => {
            return response;
        });
    };

    const jobsiteOverview = React.useMemo(
        () => (
            <div className="h-full w-full overflow-hidden rounded-lg">
                <JobsiteOverview showHeader={false} project={projectInfo} />
            </div>
        ),
        [projectInfo]
    );

    const deliveriesList = React.useMemo(
        () => (
            <div className="h-full w-full overflow-hidden rounded-lg">
                <CommandListView
                    project={projectInfo}
                    data={commandCenterSection?.sectionConfig[currentTab]?.data.deliveries}
                />
            </div>
        ),
        [projectInfo, currentTab, commandCenterSection]
    );

    const fullWeekCalendar = React.useMemo(
        () => (
            <CommandWeekCalendar
                project={projectInfo}
                data={commandCenterSection?.sectionConfig[currentTab]?.data.deliveries}
            />
        ),
        [projectInfo, currentTab, commandCenterSection]
    );

    const dayCalendar = React.useMemo(
        () => (
            <CommandDayCalendar
                project={projectInfo}
                data={commandCenterSection?.sectionConfig[currentTab]?.data.deliveries ?? []}
                numberOfDays={commandCenterSection?.sectionConfig[currentTab]?.data.number_of_days ?? 1}
            />
        ),
        [projectInfo, currentTab, commandCenterSection]
    );

    const procurementChecklist = React.useMemo(
        () => (
            <div className="h-full w-full overflow-hidden rounded-lg">
                <DashboardProcurementChecklist
                    project={projectInfo}
                    data={commandCenterSection?.sectionConfig[currentTab].data}
                />
            </div>
        ),
        [commandCenterSection, currentTab]
    );
    const weather = React.useMemo(
        () => (
            <div className="h-full w-full overflow-hidden rounded-lg">
                <DashboardWeather
                    project={projectInfo}
                    data={commandCenterSection?.sectionConfig[currentTab].data}
                    fetchWeatherData={fetchWeatherData}
                />
            </div>
        ),
        [projectInfo, currentTab, commandCenterSection]
    );

    const currentConditions = React.useMemo(
        () => (
            <div className="h-full w-full overflow-hidden rounded-lg">
                <DashboardCurrentConditions
                    projectInfo={projectInfo}
                    data={commandCenterSection?.sectionConfig[currentTab]?.data}
                    fetchWeatherData={fetchWeatherData}
                />
            </div>
        ),
        [projectInfo, currentTab, commandCenterSection]
    );

    const customText = React.useMemo(
        () => (
            <div className="h-full w-full overflow-hidden rounded-lg py-16 px-12">
                <CommandCustomText data={commandCenterSection?.sectionConfig[currentTab]?.data} />
            </div>
        ),
        [currentTab, commandCenterSection]
    );

    const milestones = React.useMemo(
        () => (
            <div className="h-full w-full overflow-hidden rounded-lg">
                <CommandMilestones
                    data={commandCenterSection?.sectionConfig[currentTab]?.data}
                    label={commandCenterSection?.sectionConfig[currentTab]?.name || "Upcoming Milestones"}
                />
            </div>
        ),
        [currentTab, commandCenterSection]
    );

    const image = React.useMemo(
        () => (
            <div className="h-full w-full overflow-hidden rounded-lg">
                <CommandImage data={commandCenterSection?.sectionConfig[currentTab]?.data} />
            </div>
        ),
        [currentTab, commandCenterSection]
    );

    // Maps section types to components
    const componentMapping = (sectionConfig: { view: string; config: any; filters: any }) => {
        switch (sectionConfig.view) {
            // Map is always mounted and handled elsewhere per ENG-113
            case "projectOverview":
                return jobsiteOverview;
            case "deliveriesWeek":
                return fullWeekCalendar;
            case "deliveriesDay":
                return dayCalendar;
            case "deliveriesList":
                return deliveriesList;
            case "procurementChecklist":
                return procurementChecklist;
            case "weather":
                return weather;
            case "currentConditions":
                return currentConditions;
            case "iframe":
                return <CommandCenterIframe iframeConfig={commandCenterSection?.sectionConfig[currentTab].data} />;
            case "customText":
                return customText;
            case "milestones":
                return milestones;
            case "image":
                return image;
            default:
                return null;
        }
    };

    const tabClickHandler = (tab: number) => {
        context?.setRunInterval(false);
        setCurrentTab(tab);
    };

    if (!projectInfo || !commandCenterSection) {
        return null;
    }

    const singleFlag = commandCenterSection?.sectionConfig.length === 1;

    useEffect(() => {
        if (context?.intervalUpdate) {
            setCurrentTab((prevTab) => (prevTab + 1) % commandCenterSection?.sectionConfig.length);
        }
    }, [context?.intervalUpdate]);

    const { commandMap } = useMap();

    useEffect(() => {
        if (commandCenterSection?.sectionConfig[currentTab].view.includes("Map")) {
            setCurrentMapTab(currentTab);
            // Trigger map resize when map tab becomes visible
            commandMap?.getMap()?.resize();
        }
    }, [currentTab, commandCenterSection]);

    // Do not show the column if the section is disabled
    if (!commandCenterSection.enabled) {
        return null;
    }

    return (
        <div className="flex flex-col flex-1 w-1/3 h-full">
            <div className="flex flex-col flex-1 h-full overflow-hidden ">
                <div
                    className="flex flex-row mb-2 items-center gap-2 flex-nowrap overflow-y-hidden no-scrollbar"
                    style={{ overflowX: "auto", maxWidth: "100%" }}
                >
                    {commandCenterSection.sectionConfig.map(
                        (config: { view: string; name: string; config: any; filters: any }, idx: number) => {
                            const isActive = idx === currentTab;
                            return (
                                <div
                                    key={idx}
                                    ref={
                                        isActive
                                            ? (el) => {
                                                  if (el) {
                                                      el.scrollIntoView({
                                                          behavior: "smooth",
                                                          inline: "center",
                                                          block: "nearest",
                                                      });
                                                  }
                                              }
                                            : undefined
                                    }
                                >
                                    {componentLabel(config, isActive, () => tabClickHandler(idx), singleFlag)}
                                </div>
                            );
                        }
                    )}
                </div>
                {componentMapping(commandCenterSection.sectionConfig[currentTab])}
                {/* Always have the map mounted in the DOM. Only set to visible when the map tab is selected. 
                This reduces unnecessary remounting of the map and allows us to maintain map state (like zoom and position) when users switch between tabs.
                ENG-113
                 */}

                {commandCenterSection.sectionConfig.some((config) => config.view.includes("Map")) && (
                    <div
                        id={`map-mount-container_${commandCenterSection.id}`}
                        className={Helper.classNames(
                            "flex flex-col flex-1 h-full overflow-hidden",
                            commandCenterSection.sectionConfig[currentTab].view.includes("Map") ? "" : "w-0 h-0"
                        )}
                    >
                        <CommandCenterMapContainer>
                            <CommandCenterMap
                                data={commandCenterSection?.sectionConfig[currentMapTab]?.data}
                                project={projectInfo}
                                map_objects={commandCenterSection?.sectionConfig[currentMapTab]?.data.map_objects || []}
                                pixelRatio={context?.devicePixelRatio}
                            />
                            <CommandMapLegend
                                project={projectInfo}
                                data={commandCenterSection?.sectionConfig[currentMapTab]?.data || []}
                            />
                        </CommandCenterMapContainer>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CommandCenterColumnView;
